import { useCallback, useEffect, useState } from 'react'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  type User,
} from 'firebase/auth'
import { auth } from './firebase'
import { api } from '../api/client'

type VerifyState = 'idle' | 'verifying' | 'verified' | 'unprovisioned' | 'error'

export type AuthState = {
  ready: boolean
  user: User | null
  verifyState: VerifyState
  errorMessage: string | null
}

let listeners = new Set<(s: AuthState) => void>()
let state: AuthState = {
  ready: false,
  user: null,
  verifyState: 'idle',
  errorMessage: null,
}

function setState(next: Partial<AuthState>) {
  state = { ...state, ...next }
  listeners.forEach((fn) => fn(state))
}

let unsub: (() => void) | null = null

function ensureListener() {
  if (unsub) return
  unsub = onAuthStateChanged(auth, async (user) => {
    if (!user) {
      setState({ ready: true, user: null, verifyState: 'idle', errorMessage: null })
      return
    }
    setState({ ready: true, user, verifyState: 'verifying', errorMessage: null })
    try {
      const { error, response } = await api.POST('/api/auth/verify' as never, {} as never)
      if (response.status === 403) {
        setState({ verifyState: 'unprovisioned' })
        return
      }
      if (error || !response.ok) {
        setState({
          verifyState: 'error',
          errorMessage: 'Backend verification failed. Try again or contact the operator.',
        })
        return
      }
      setState({ verifyState: 'verified' })
    } catch (err) {
      setState({
        verifyState: 'error',
        errorMessage: err instanceof Error ? err.message : 'Network error',
      })
    }
  })
}

export function useAuth(): AuthState & {
  signIn: (email: string, password: string) => Promise<void>
  signOutNow: () => Promise<void>
} {
  const [snapshot, setSnapshot] = useState<AuthState>(state)

  useEffect(() => {
    ensureListener()
    listeners.add(setSnapshot)
    setSnapshot(state)
    return () => {
      listeners.delete(setSnapshot)
    }
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    setState({ verifyState: 'verifying', errorMessage: null })
    try {
      await signInWithEmailAndPassword(auth, email, password)
    } catch (err) {
      setState({
        verifyState: 'error',
        errorMessage: err instanceof Error ? err.message : 'Sign-in failed',
      })
      throw err
    }
  }, [])

  const signOutNow = useCallback(async () => {
    await signOut(auth)
  }, [])

  return { ...snapshot, signIn, signOutNow }
}
